import ContentPreview from '@/components/ContentPreview'
import { getErrorMessage } from '@/lib/error'
import { FormattedTimestamp } from '@/components/FormattedTimestamp'
import UserAvatar from '@/components/UserAvatar'
import { toNote } from '@/lib/link'
import { useSecondaryPage } from '@/PageManager'
import { determineTargetRelays, publishEventAndDisplay } from '@/services/client.service'
import { useState, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { discardPendingEvent, savePendingEvent, pendingEventsAtom } from '@/atoms/pending'
import { hasUnsavedNsec, openSaveNsecModal, recordNotePublishedDay } from '@/atoms/key-backup'
import * as kinds from '@nostr/tools/kinds'
import { useAtomValue } from 'jotai'

export type TPendingListRef = {
  publishAll: () => Promise<void>
  hasItems: boolean
  isPublishingAll: boolean
}

const PendingList = forwardRef<TPendingListRef>((_, ref) => {
  const { t } = useTranslation()
  const { push } = useSecondaryPage()
  const pendingEvents = useAtomValue(pendingEventsAtom)
  const [isPublishingAll, setIsPublishingAll] = useState(false)

  async function publishAll() {
    if (pendingEvents.length === 0 || isPublishingAll) return

    setIsPublishingAll(true)

    const errors: string[] = []

    for (const event of pendingEvents) {
      try {
        const relayUrls = await determineTargetRelays(event)
        await publishEventAndDisplay(relayUrls, event)
        if (
          (event.kind === kinds.ShortTextNote || event.kind === kinds.Comment) &&
          recordNotePublishedDay(event.created_at) &&
          hasUnsavedNsec()
        ) {
          openSaveNsecModal()
        }
        await discardPendingEvent(event.id)
      } catch (error) {
        await savePendingEvent(event)
        const eventErrors = error instanceof AggregateError ? error.errors : [error]
        errors.push(...eventErrors.map(getErrorMessage))
      }
    }

    if (errors.length > 0) {
      console.warn('failed to post:', errors[0])
    }

    setIsPublishingAll(false)
  }

  useImperativeHandle(ref, () => ({
    publishAll,
    hasItems: pendingEvents.length > 0,
    isPublishingAll
  }))

  if (pendingEvents.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-muted-foreground">{t('No pending items')}</div>
    )
  }

  return (
    <div className="pt-2">
      {pendingEvents.map((event) => (
        <button
          key={event.id}
          className="flex items-center justify-between cursor-pointer py-2 px-4 w-full text-left hover:bg-muted/40 transition-colors"
          onClick={() => push(toNote(event))}
        >
          <div className="flex gap-2 items-center flex-1 w-0">
            <UserAvatar user={event.pubkey} size="small" />
            <ContentPreview className="truncate flex-1 w-0 text-muted-foreground" event={event} />
          </div>
          <div className="text-muted-foreground shrink-0">
            <FormattedTimestamp timestamp={event.created_at} short />
          </div>
        </button>
      ))}
    </div>
  )
})

PendingList.displayName = 'PendingList'

export default PendingList
